import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

import { PRIVATE_ROUTES } from '@/app/private/private.routes';
import { CourseService } from '@/courses/services/courses.service';

@Component({
  name: 'ed-subject-building-process'
})
export default class EdSubjectBuildingProcessComponent extends Vue {
  @Prop() readonly contentId: number;
  @Prop({ default: null }) readonly continueRoute: string;
  @Prop({ default: false }) readonly processFailed: boolean;

  logs: string = 'Waiting...';
  loading: boolean = true;
  value = 0;
  hasError = false;
  since = 0;
  limit = 200;
  size = 200;
  notStarted = true;
  componentDestroyed = false;
  
  get subjectCreated(): boolean {
    return this.value === 100;
  }
  
  get progressBarColor() {
    if (this.hasError) {
      return 'var(--ed-color-danger)';
    }

    if (this.notStarted) {
      return 'var(--ed-color-medium)';
    }

    return 'var(--ed-color-main)';
  }

  get processLabel(): string {
    const creatingLabel = this.$i18n.t('courses.subjectBuilder.creatingSubjectLabel').toString();
    const createdLabel = this.$i18n.t('courses.subjectBuilder.createdSubjectLabel').toString();
    const notCreatedLabel = this.$i18n.t('courses.subjectBuilder.errorSubjectLabel').toString();
    const notStartedLabel = this.$i18n.t('courses.subjectBuilder.notStartedLabel').toString();

    if(this.notStarted) {
      return notStartedLabel;
    }

    if (this.hasError) {
      return notCreatedLabel;
    }

    if (this.subjectCreated) {
      return createdLabel;
    } else {
      return creatingLabel;
    }
  }

  onContinue() {
    if (this.continueRoute) {
      this.$router.push({ path: this.continueRoute });
    } else {
      this.$emit('onContinue');
    }
  }

  goToSubject() {
    this.$router.push({
      name: PRIVATE_ROUTES.COURSE.NAME,
      params: {
        courseId: this.contentId.toString()
      }
    });
    this.$emit('onContinue');
  }

  setError() {
    this.hasError = true;
    this.value = 0;
    this.loading = false;
  }

  async getLogs(contentId: number = null) {
    const subjectId = contentId || this.contentId;

    const limit = this.processFailed ? '*' : this.limit;
    const {
      log,
      currentLength,
      currentEnd
    }  = await CourseService.getLogsFromSubjectBuilder(subjectId, this.since, limit);
    
    this.notStarted = false;

    const isEnd = log.includes('<END>');
    const isStart = log.includes('<START>');
    const isError = log.includes('<ERROR>');

    if (isStart) {
      this.logs = '';
    }

    if (currentLength) {
      const lines = log.split('\n').filter(l => l !== '<ERROR>' && l !== '<START>' && l !== '<END>').join('\n');
  
      const logsCleaned = this.logs.trim().split('\n').join('');
      const linesCleaned = lines.trim().split('\n').join('');
  
      if (logsCleaned !== linesCleaned) {
        if (!this.processFailed) {
          const linesToPrint = lines.split('\n');
          
          for (let i = 0; i < linesToPrint.length; i++) {
            await this.delay(100);
            this.logs = `${linesToPrint[i]}\n` + this.logs;
          }
        } else {
          this.logs =lines.split('\n').reverse().join('\n');
        }
      }
  
      if (isEnd) {
        this.loading = false;
        this.value = 100;
        return;
      }
  
      if (isError) {
        this.setError();
        return;
      }
  
      if (currentEnd < Number(this.limit)) {
        this.since = currentEnd + 1;
      } else {
        this.since += this.size;
      }
      
      const newLimit = this.since + this.size;
      this.limit = newLimit >= currentLength ? null : newLimit;
    } else {
      await this.delay(1000);
    }

    if (!this.componentDestroyed) {
      this.getLogs(subjectId);
    }
  }

  async delay(time: number): Promise<void> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve();
      }, time);
    });
  } 

  beforeDestroy() {
    this.componentDestroyed = true;
  }

  created() {
    this.loading = !this.processFailed;
  }

}